/**
 * Updates a node optimistically in the ingredients tree structure.
 * This function updates a temporary node in the tree before the backend confirms.
 *
 * @param {Object} tree - The current ingredients tree object.
 * @param {Object} nodeData - The node data to update.
 * @param {string} nodeData._id - The ID of the node (remains unchanged).
 * @param {string} nodeData._path - The path of the node (remains unchanged).
 * @param {string} nodeData.name - The name of the node.
 * @param {Object} nodeData.translations - The translations of the node.
 * @param {string} nodeData.eNumber - The E number of the node.
 * @param {string[]} nodeData.synonyms - The synonyms of the node.
 * @param {string[]} nodeData.hiddenSynonyms - The hidden synonyms of the node.
 * @param {string} nodeData.description - The description of the node.
 * @param {string[]} nodeData.productsIds - The IDs of the products associated with the node.
 * @param {boolean} nodeData.isIngredient - Whether the node is an ingredient.
 * @param {boolean} nodeData.isDeclared - Whether the node is declared.
 * @param {boolean} nodeData.isVisibleInRecipes - Whether the node is visible in recipes.
 * @param {string} nodeData.visibility - Visibility level.
 * @returns {Object} - The updated tree with the node updated.
 */
const updateNodeInTree = (tree, nodeData) => {
  if (!tree || typeof tree !== 'object') {
    return tree
  }

  if (!nodeData._id || !nodeData._path) {
    console.error('updateNodeInTree: _id and _path are required', {
      nodeData
    })
    return tree
  }

  // Create a deep copy of the tree
  const updatedTree = JSON.parse(JSON.stringify(tree))

  // Helper function to find node by path (similar to addNodeToTree logic)
  const findNodeByPath = (tree, path) => {
    if (!path) return null

    // Parse path: "ingredientsTree/animal/children/meat" -> ["ingredientsTree", "animal", "children", "meat"]
    const pathParts = path.split('/').filter(Boolean)

    // Skip 'ingredientsTree' - it's the root key
    let currentNode = tree
    let i = pathParts[0] === 'ingredientsTree' ? 1 : 0

    while (i < pathParts.length) {
      const part = pathParts[i]

      // Skip 'children' - it's just a separator
      if (part === 'children') {
        i++
        continue
      }

      // Navigate to the next level
      if (currentNode && currentNode[part]) {
        currentNode = currentNode[part]
        i++
      } else {
        // Path not found
        return null
      }
    }

    return currentNode
  }

  // Find the node to update
  const nodeToUpdate = findNodeByPath(updatedTree, nodeData._path)

  if (nodeToUpdate) {
    // Update only content fields (NOT _id, _path, _parentIds, children, isTopLevel, isLastLevel)
    if (nodeData.name !== undefined) {
      nodeToUpdate.name = nodeData.name
    }
    if (nodeData.translations !== undefined) {
      nodeToUpdate.translations = nodeData.translations
    }
    if (nodeData.eNumber !== undefined) {
      nodeToUpdate.eNumber = nodeData.eNumber
    }
    if (nodeData.synonyms !== undefined) {
      nodeToUpdate.synonyms = nodeData.synonyms
    }
    if (nodeData.hiddenSynonyms !== undefined) {
      nodeToUpdate.hiddenSynonyms = nodeData.hiddenSynonyms
    }
    if (nodeData.description !== undefined) {
      nodeToUpdate.description = nodeData.description
    }
    if (nodeData.isIngredient !== undefined) {
      nodeToUpdate.isIngredient = nodeData.isIngredient
    }
    if (nodeData.isDeclared !== undefined) {
      nodeToUpdate.isDeclared = nodeData.isDeclared
    }
    if (nodeData.isVisibleInRecipes !== undefined) {
      nodeToUpdate.isVisibleInRecipes = nodeData.isVisibleInRecipes
    }
    if (nodeData.productsIds !== undefined) {
      nodeToUpdate.productsIds = nodeData.productsIds
    }
    if (nodeData.visibility !== undefined) {
      nodeToUpdate.visibility = nodeData.visibility
    }

    // Update metadata
    const currentDate = new Date().toISOString()
    nodeToUpdate._updatedAt = currentDate
    nodeToUpdate._isUpdated = true
  } else {
    console.warn('updateNodeInTree: Node not found at path', {
      path: nodeData._path,
      nodeId: nodeData._id
    })
  }

  // Also update identical nodes (same _id in different locations)
  const updateIdenticalNodes = (node) => {
    if (!node || typeof node !== 'object') {
      return
    }

    // Check if this node has the same _id
    if (node._id === nodeData._id && node._path !== nodeData._path) {
      // Update content fields only
      if (nodeData.name !== undefined) {
        node.name = nodeData.name
      }
      if (nodeData.translations !== undefined) {
        node.translations = nodeData.translations
      }
      if (nodeData.eNumber !== undefined) {
        node.eNumber = nodeData.eNumber
      }
      if (nodeData.synonyms !== undefined) {
        node.synonyms = nodeData.synonyms
      }
      if (nodeData.hiddenSynonyms !== undefined) {
        node.hiddenSynonyms = nodeData.hiddenSynonyms
      }
      if (nodeData.description !== undefined) {
        node.description = nodeData.description
      }
      if (nodeData.isIngredient !== undefined) {
        node.isIngredient = nodeData.isIngredient
      }
      if (nodeData.isDeclared !== undefined) {
        node.isDeclared = nodeData.isDeclared
      }
      if (nodeData.isVisibleInRecipes !== undefined) {
        node.isVisibleInRecipes = nodeData.isVisibleInRecipes
      }
      if (nodeData.productsIds !== undefined) {
        node.productsIds = nodeData.productsIds
      }
      if (nodeData.visibility !== undefined) {
        node.visibility = nodeData.visibility
      }

      // Update metadata
      const currentDate = new Date().toISOString()
      node._updatedAt = currentDate
      node._isUpdated = true
    }

    // Recursively check children
    if (node.children) {
      Object.values(node.children).forEach((child) => {
        updateIdenticalNodes(child)
      })
    }
  }

  // Update all identical nodes in the tree
  Object.values(updatedTree).forEach((rootNode) => {
    updateIdenticalNodes(rootNode)
  })

  return updatedTree
}

export default updateNodeInTree
